import {
  CrownSimpleIcon,
  GiftIcon,
  TrophyIcon,
  UserCircleCheckIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";

import { useAccountCurrency } from "@/client/services/currency/currency";

import { DashboardContent } from "../../components/layout/dashboard-content";
import { ReferralLinkCard } from "./components/referral-link-card";
import { ReferralsList } from "./components/referrals-list";
import { useReferralDashboard } from "./hooks/use-referral-dashboard";
import type { ReferralStatusFilter } from "./types/referral";

type MetricCardProps = {
  label: string;
  value: string | number;
  tone: "indigo" | "emerald" | "amber";
  icon: React.ElementType;
};

const MetricCard = ({ label, value, tone, icon: Icon }: MetricCardProps) => {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-500",
  };

  return (
    <article className="flex min-h-[88px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <span className={cn("grid size-11 shrink-0 place-items-center rounded-full", tones[tone])}>
        <Icon aria-hidden size={23} weight="duotone" />
      </span>

      <div className="min-w-0">
        <p className="text-[11px] font-medium text-slate-600 sm:text-xs">{label}</p>

        <p
          className={cn(
            "mt-1 truncate text-xl font-bold sm:text-2xl",
            tone === "emerald" ? "text-emerald-600" : "text-indigo-950",
          )}
        >
          {value}
        </p>
      </div>
    </article>
  );
};

const LoadingState = () => {
  return (
    <div className="animate-pulse space-y-3">
      <div className="h-24 rounded-2xl bg-slate-200/70" />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="h-24 rounded-2xl bg-slate-200/70" />
        <div className="h-24 rounded-2xl bg-slate-200/70" />
        <div className="h-24 rounded-2xl bg-slate-200/70" />
      </div>

      <div className="h-72 rounded-2xl bg-slate-200/70" />
    </div>
  );
};

export const ReferralsPage = () => {
  const { format } = useAccountCurrency();

  const [selectedFilter, setSelectedFilter] = useState<ReferralStatusFilter>("all");

  const [page, setPage] = useState(1);

  const { data, loading, error } = useReferralDashboard(page, selectedFilter);

  // Server already filters by status — no client-side filter needed.
  // Keep reference stable to avoid unnecessary re-renders.
  const filteredReferrals = data.referrals;

  const handleFilterChange = (filter: ReferralStatusFilter) => {
    setSelectedFilter(filter);
    setPage(1);
  };

  return (
    <DashboardContent>
      <section className="min-w-0 space-y-3.5">
        <header>
          <h1 className="text-xl font-bold tracking-tight text-indigo-950 sm:text-2xl">
            Parrainage
          </h1>

          <p className="mt-1 text-xs leading-5 text-slate-600 sm:text-[13px]">
            Invitez vos proches à découvrir Mon CV Pro et recevez des récompenses.
          </p>
        </header>

        {loading ? (
          <LoadingState />
        ) : error ? (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
            <p className="text-xs font-semibold text-rose-800">
              Impossible de charger vos parrainages
            </p>

            <p className="mt-1 text-[11px] leading-4 text-rose-700">
              Vérifiez que le backend est démarré, puis actualisez la page.
            </p>
          </div>
        ) : (
          <>
            <ReferralLinkCard isEligible={data.isEligible} referralLink={data.referralLink} />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <MetricCard
                label="Nombre de filleuls"
                value={data.totalReferrals}
                tone="indigo"
                icon={UsersThreeIcon}
              />

              <MetricCard
                label="Filleuls actifs"
                value={data.activeReferrals}
                tone="emerald"
                icon={UserCircleCheckIcon}
              />

              <MetricCard
                label="Récompense de parrainage"
                value={format(data.rewardAmount)}
                tone="amber"
                icon={GiftIcon}
              />
            </div>

            <div className="grid gap-3 lg:grid-cols-2">
              <div className="flex min-h-12 items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5">
                <TrophyIcon
                  aria-hidden
                  size={23}
                  weight="duotone"
                  className="shrink-0 text-emerald-600"
                />

                <p className="text-[11px] leading-4 text-emerald-950 sm:text-xs">
                  À partir de <strong>{data.policy?.unpaidThreshold ?? 10} filleuls actifs</strong>,
                  vous gagnez{" "}
                  <strong>{format(data.policy?.rewardPerActiveReferralFcfa ?? 250)}</strong> de
                  récompense par nouveau filleul actif.
                </p>
              </div>

              <div className="flex min-h-12 items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5">
                <CrownSimpleIcon
                  aria-hidden
                  size={23}
                  weight="fill"
                  className="shrink-0 text-amber-500"
                />

                <p className="text-[11px] leading-4 text-indigo-950 sm:text-xs">
                  À partir de{" "}
                  <strong>{data.policy?.smartProMilestone ?? 15} filleuls actifs</strong>, un pack{" "}
                  <strong>SMART PRO</strong> vous est offert.
                </p>
              </div>
            </div>

            <ReferralsList
              referrals={filteredReferrals}
              activeCount={data.activeReferrals}
              inactiveCount={data.inactiveReferrals}
              filter={selectedFilter}
              page={page}
              onFilterChange={handleFilterChange}
              onPageChange={setPage}
            />
          </>
        )}
      </section>
    </DashboardContent>
  );
};
